/**
 * telemetry.js — What people actually do in the portal.
 *
 * API calls log themselves server-side (server/lib/usageTelemetry.js). This file
 * covers the things the server cannot see:
 *   - which screen someone opened (page views, on route change)
 *   - which feature they invoked (track('quote.sent', {...}))
 *   - which control they clicked (every button, tab, menu item, toggle — automatic)
 *
 * Events are batched and flushed on a timer, on page hide, and on unload, so a
 * click that immediately navigates away still gets recorded. Fire-and-forget:
 * telemetry must never be able to break or slow down the thing it measures.
 *
 * This is NOT the audit trail. Mutations are recorded server-side in action_log
 * and kept forever; these events are pruned after 90 days.
 */
import { api } from './api';

const FLUSH_INTERVAL_MS = 10_000;
const FLUSH_AT = 20;
const MAX_QUEUE = 200;

let queue = [];
let timer = null;

function schedule() {
  if (timer) return;
  timer = setTimeout(() => { timer = null; flush(); }, FLUSH_INTERVAL_MS);
}

function enqueue(evt) {
  try {
    if (queue.length >= MAX_QUEUE) return;
    queue.push(evt);
    if (queue.length >= FLUSH_AT) flush();
    else schedule();
  } catch { /* never throw from telemetry */ }
}

/** Send whatever is queued. Safe to call at any time. */
export function flush() {
  if (!queue.length) return;
  const events = queue;
  queue = [];
  if (timer) { clearTimeout(timer); timer = null; }
  api.post('/usage', { events }).catch(() => { /* drop on the floor */ });
}

/**
 * Best-effort flush when the tab is going away. sendBeacon survives unload,
 * which a fetch generally does not.
 */
function flushBeacon() {
  if (!queue.length) return;
  const events = queue;
  queue = [];
  try {
    const blob = new Blob([JSON.stringify({ events })], { type: 'application/json' });
    if (navigator.sendBeacon && navigator.sendBeacon('/api/usage', blob)) return;
  } catch { /* fall through */ }
  api.post('/usage', { events }).catch(() => {});
}

/**
 * Record a named feature event.
 *
 *   track('quote.sent', { mode: 'virtour', total: 1300 })
 *
 * Keep `props` small and non-sensitive — it is bounded server-side at 2KB and
 * lands in a table several people can read.
 *
 * @param {string} event  dotted name, e.g. 'quote.sent', 'pdf.export'
 * @param {object} [props]
 */
export function track(event, props) {
  if (!event) return;
  enqueue({
    kind: 'feature',
    event: String(event).slice(0, 120),
    page: typeof window !== 'undefined' ? window.location.pathname : null,
    props: props && typeof props === 'object' ? props : undefined,
  });
}

/** Record a page view. Called automatically on route change. */
export function trackPageView(path, extra) {
  enqueue({
    kind: 'page_view',
    page: String(path || '').slice(0, 200),
    props: extra && typeof extra === 'object' ? extra : undefined,
  });
}

// ── Click capture ────────────────────────────────────────────
// One capture-phase listener answers "is anyone using this button" for every
// control without instrumenting each one. A `data-track` attribute on the control
// (or any ancestor) names it explicitly and wins over the visible label. The
// nearest `data-focus` ancestor (a Focus-hideable element) is recorded too, so
// each Focus key's real use can be counted.

const CLICKABLE = [
  '[data-track]', 'button', 'a[href]', 'summary', 'input[type="checkbox"]', 'input[type="radio"]',
  '[role="button"]', '[role="tab"]', '[role="menuitem"]', '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]', '[role="switch"]', '[role="checkbox"]', '[role="radio"]',
  '[role="option"]', '[role="link"]',
].join(',');

const clean = (s) => String(s || '')
  .replace(/\s+/g, ' ')
  .trim()
  // Amounts, counts and dates vary per record — strip them so "Pay $1,240" and
  // "Pay $980" aggregate as one control, and no figures land in the table.
  .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '@')
  .replace(/\$?\d[\d,.:/-]*%?/g, '#')
  .slice(0, 60);

// Free text on a control can be a record — a client's name on a row button, a
// typed clause note in a tooltip. Only short, label-like text is used as the name;
// anything longer, or joined with row separators, falls back to the icon or role.
// Controls that display a record carry a static data-track name instead.
function shortLabel(s) {
  const t = clean(s);
  if (!t || t.length > 30 || /[·•●|—–]/.test(t)) return '';
  return t.split(' ').filter((w) => /\p{L}/u.test(w)).length > 3 ? '' : t;
}

function controlLabel(el) {
  const explicit = el.closest('[data-track]');
  if (explicit) return explicit.getAttribute('data-track');
  // A picked option is a record (a client, a job), not a control — the dropdown is.
  const role = el.getAttribute('role');
  if (role === 'option') return 'option';
  if (el.tagName === 'INPUT') {
    const label = el.labels?.[0] || el.closest('label');
    return shortLabel(label?.textContent) || `${el.type}`;
  }
  // No `title` fallback: it is the attribute most often built from record content.
  const text = shortLabel(el.getAttribute('aria-label')) || shortLabel(el.innerText || el.textContent);
  if (text) return text;
  const icon = el.querySelector('svg[data-testid]');
  return icon ? icon.getAttribute('data-testid') : (role || el.tagName.toLowerCase());
}

function controlArea(el) {
  const area = el.closest('[data-track-area]');
  if (area) return area.getAttribute('data-track-area');
  // Portaled dialogs sit outside the page's area. Their headings can name a record
  // (an address, a project), so only the kind is kept.
  return el.closest('[role="dialog"], [role="presentation"]') ? 'dialog' : undefined;
}

let lastClick = { key: '', at: 0 };

function onClick(e) {
  try {
    const target = e.target instanceof Element ? e.target : null;
    const el = target?.closest(CLICKABLE);
    if (!el || el.closest('[data-track-ignore]')) return;
    const event = controlLabel(el);
    const page = window.location.pathname;
    // A <label> click re-dispatches to its input — count the gesture once.
    const key = `${page}|${event}`;
    const now = Date.now();
    if (key === lastClick.key && now - lastClick.at < 400) return;
    lastClick = { key, at: now };

    const role = el.getAttribute('role') || el.tagName.toLowerCase();
    const tab = new URLSearchParams(window.location.search).get('tab') || undefined;
    const area = controlArea(el);
    const focus = el.closest('[data-focus]')?.getAttribute('data-focus') || undefined;
    let ext;
    // A named link says what it is; its host may be a prospect's own site.
    if (el.tagName === 'A' && el.host && el.host !== window.location.host && !el.closest('[data-track]')) ext = el.host;
    enqueue({
      kind: 'click', event, page,
      props: { role, ...(area && { area }), ...(tab && { tab }), ...(focus && { focus }), ...(ext && { ext }) },
    });
  } catch { /* never throw from telemetry */ }
}

// Form fields are used, not clicked. Inside a Focus key, a change (or focusing an
// autocomplete, which picks values without a change event) counts as a use of
// that key. The value is never read.
const FIELD = 'select, textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="password"])';

function onField(e) {
  try {
    const el = e.target instanceof Element ? e.target : null;
    if (!el || !el.matches(FIELD)) return;
    if (e.type !== (el.getAttribute('role') === 'combobox' ? 'focusin' : 'change')) return;
    const focus = el.closest('[data-focus]')?.getAttribute('data-focus');
    if (!focus || el.closest('[data-track-ignore]')) return;
    const page = window.location.pathname;
    const key = `${page}|field|${focus}`;
    const now = Date.now();
    if (key === lastClick.key && now - lastClick.at < 400) return;
    lastClick = { key, at: now };

    const tab = new URLSearchParams(window.location.search).get('tab') || undefined;
    const area = controlArea(el);
    enqueue({
      kind: 'click', event: 'field', page,
      props: { role: 'input', focus, ...(area && { area }), ...(tab && { tab }) },
    });
  } catch { /* never throw from telemetry */ }
}

let listenersBound = false;

/** Bind the flush-on-hide, click and field listeners once, at app start. */
export function initTelemetry() {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushBeacon();
  });
  window.addEventListener('pagehide', flushBeacon);
  document.addEventListener('click', onClick, { capture: true, passive: true });
  document.addEventListener('change', onField, { capture: true, passive: true });
  document.addEventListener('focusin', onField, { capture: true, passive: true });
}
